// src/shared/table-cards.js
//
// v134.3 - admin tables become cards on a phone (the 15 Oct 2026 look-and-feel review: wide tables only scrolled
// sideways below 700px). Each cell of a table inside .table-wrap gets a data-label copied from its column heading;
// admin.html's phone-width CSS then shows every row as a card of "Heading: value" lines. Wide screens are unchanged.
//
// Cells keep any label they already have, so a table can set its own. Runs once on load and again whenever the
// page adds rows (the tables are filled by script), batched to one pass per frame.
//
// v136.8 (the 16 Oct 2026 render audit): it watched the WHOLE page and re-read every table on every change, on a
// desktop too, where the labels are never shown. Now it runs only while the screen is phone width
// (PHONE_QUERY; it starts on crossing into it and stops on crossing out), and a row it has labelled is skipped
// next time unless its cells were replaced (the row remembers its first cell).

export const PHONE_QUERY = '(max-width: 700px)';

// row -> the first cell it had when labelled; a rebuilt row (new cells) has a different first cell
const _done = (typeof WeakMap !== 'undefined') ? new WeakMap() : null;

export function labelTable(table) {
  const headRow = table && table.querySelector ? table.querySelector('thead tr') : null;
  if (!headRow) return 0;
  let heads = null;   // read only when a row needs it
  let n = 0;
  table.querySelectorAll('tbody tr').forEach(function (tr) {
    if (_done && tr.firstElementChild && _done.get(tr) === tr.firstElementChild) return;   // labelled already
    if (!heads) heads = Array.from(headRow.children).map(function (th) { return (th.textContent || '').trim(); });
    let col = 0;
    Array.from(tr.children).forEach(function (td) {
      if (!td.hasAttribute('data-label') && heads[col]) { td.setAttribute('data-label', heads[col]); n++; }
      col += parseInt(td.getAttribute('colspan') || '1', 10) || 1;
    });
    if (_done && tr.firstElementChild) _done.set(tr, tr.firstElementChild);
  });
  return n;
}

export function labelAll(root) {
  const r = root || (typeof document !== 'undefined' ? document : null);
  if (!r || !r.querySelectorAll) return 0;
  let n = 0;
  r.querySelectorAll('.table-wrap table').forEach(function (t) { n += labelTable(t); });
  return n;
}

// The wiring, exported so a test can drive it with a fake window. Returns { active() } for the test.
export function startTableCards(win, doc) {
  if (!win || !doc || typeof win.MutationObserver === 'undefined') return null;
  const mql = (typeof win.matchMedia === 'function') ? win.matchMedia(PHONE_QUERY) : null;
  let observer = null;
  let queued = false;
  const run = function () { queued = false; if (observer) labelAll(doc); };
  const queue = function () { if (!queued) { queued = true; (win.requestAnimationFrame || setTimeout)(run); } };
  const on = function () {
    if (observer || !doc.body) return;
    labelAll(doc);
    observer = new win.MutationObserver(queue);
    observer.observe(doc.body, { childList: true, subtree: true });
  };
  const off = function () { if (observer) { observer.disconnect(); observer = null; } };
  const apply = function () { if (!mql || mql.matches) on(); else off(); };   // no matchMedia at all: behave as before
  const start = function () {
    apply();
    if (mql) {
      if (typeof mql.addEventListener === 'function') mql.addEventListener('change', apply);
      else if (typeof mql.addListener === 'function') mql.addListener(apply);
    }
  };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start); else start();
  return { active: function () { return !!observer; } };
}

if (typeof window !== 'undefined' && typeof document !== 'undefined' && typeof MutationObserver !== 'undefined') {
  startTableCards(window, document);
}
